/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { createSelector, createSlice } from 'redux-starter-kit';

import { fetchTemplate, fetchTemplates, updateTemplate } from '../api/index.js';
import { supportLoadingStates } from './utils.js';

/**
 * `templates` slice for managing template-level data.
 *
 * SLICE DEPENDENCIES:
 *  [ NONE ]
 */

const { initialState, caseReducers } = supportLoadingStates(['fetchAllTemplates']);
export const allowedSortFields = ['createdAt', 'name'];
export const allowedDirections = ['asc', 'desc'];

const defaultState = {
  ...initialState,
  all: [],
  pagination: {
    total: 0,
    page: 1,
    limit: 30,
    totalPages: 0
  },
  searchQuery: '',
  sort: 'createdAt',
  direction: 'desc'
};

const templates = createSlice({
  slice: 'templates',
  initialState: defaultState,
  reducers: {
    ...caseReducers,
    receivedTemplates: (state, action) => {
      const { items, pagination } = action.payload;
      if (pagination.page === 1) {
        state.all = items;
      } else {
        state.all = [...state.all, ...items];
      }
      state.pagination = pagination;
    },
    receivedTemplate: (state, action) => {
      const { template: newTemplate } = action.payload;
      state.all = state.all
        .filter((template) => template.id !== newTemplate.id)
        .concat(newTemplate);
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.all = [];
      state.pagination = {
        total: 0,
        page: 1,
        limit: 30,
        totalPages: 0
      };
    },
    setSortAndDirection: (state, action) => {
      const { sort, direction } = action.payload;
      state.sort = allowedSortFields.includes(sort) ? sort : 'createdAt';
      state.direction = allowedDirections.includes(direction) ? direction : 'desc';
      state.all = [];
      state.pagination = {
        total: 0,
        page: 1,
        limit: 30,
        totalPages: 0
      };
    }
  },
});

templates.selectors.getTemplates = createSelector(
  ['templates.all'],
  (allTemplates) =>
    allTemplates.map((template) =>
      // We want these to be fully-formed JS Date objects tho, which can't be stored in state.
      ({
        ...template,
        createdAt: new Date(template.createdAt),
        modifiedAt: new Date(
          template.unpublishedVersionModifiedAt || template.publishedVersionModifiedAt,
        ),
      }),
    ),
);

templates.selectors.getTemplateById = (state, templateId) =>
  createSelector(
    [templates.selectors.getTemplates],
    (allTemplates) => _.find(allTemplates, { id: templateId }),
  )(state);

templates.selectors.getLatestVersionByTemplateId = (state, templateId) => {
  const template = templates.selectors.getTemplateById(state, templateId);
  return template ? template.unpublishedVersion || template.publishedVersion : null;
};

templates.selectors.getIsFetchingTemplates = createSelector([
  'templates.isFetchAllTemplatesInProgress',
]);

templates.selectors.getSearchQuery = createSelector(['templates.searchQuery']);

templates.selectors.getSort = createSelector(
  ['templates.sort'],
  (sort) => sort
);
templates.selectors.getDirection = createSelector(
  ['templates.direction'],
  (direction) => direction
);

templates.operations = {
  getTemplates({ page = 1, limit = 30, sort, direction } = {}) {
    return async (dispatch, getState) => {
      const state = getState();
      const searchQuery = templates.selectors.getSearchQuery(state);
      const sortField = sort || templates.selectors.getSort(state);
      const sortDirection = direction || templates.selectors.getDirection(state);

      dispatch(templates.actions.fetchAllTemplatesInProgress());
      try {
        const response = await fetchTemplates({ page, limit, search: searchQuery, sort: sortField, direction: sortDirection });
        dispatch(templates.actions.receivedTemplates(response.data));
        dispatch(templates.actions.fetchAllTemplatesCompleted());
        return response.data;
      } catch (e) {
        dispatch(templates.actions.fetchAllTemplatesFailed(e.message));
        throw e;
      }
    };
  },
  setSearchQuery(query) {
    return (dispatch) => {
      dispatch(templates.actions.setSearchQuery(query));
      return dispatch(templates.operations.getTemplates({ page: 1 }));
    };
  },
  setSortAndDirection({ sort, direction }) {
    return (dispatch) => {
      dispatch(templates.actions.setSortAndDirection({ sort, direction }));
      return dispatch(templates.operations.getTemplates({ page: 1, sort, direction }));
    };
  },
  fetchTemplateById(templateId) {
    return async (dispatch, getState) => {
      const cachedTemplate = templates.selectors.getTemplateById(getState(), templateId);
      if (cachedTemplate) {
        return cachedTemplate;
      }

      try {
        const templateRes = await fetchTemplate(templateId);
        const template = templateRes.data;
        dispatch(templates.actions.receivedTemplate({ template }));
        return template;
      } catch (e) {
        console.error(e);
        return null;
      }
    };
  },
  updateTemplate(templateId, payload) {
    return async (dispatch) => {
      try {
        const templateRes = await updateTemplate(templateId, payload);
        dispatch(templates.actions.receivedTemplate({ template: templateRes.data }));
      } catch (e) {
        console.error(e);
      }
    };
  },
  fetchRecentTemplates() {
    return async (dispatch) => {
      const response = await fetchTemplates({ sort: 'createdAt', direction: 'desc', limit: 3, page: 1 });
      return response.data.items || [];
    };
  },
};

export default templates;
